// ─────────────────────────────────────────────────────────────────────────────
// AppChrome — the brand chrome every surface in this app shares.
//
// WHY THIS FILE EXISTS (requirements F24, 2026-07-27):
// the header and the yellow page envelope were never a component. `HomePage`
// declared a private `TopHeader` and `TrivianightPage` declared a *second*,
// near-identical private `TopHeader`, and each page repeated the envelope
// (`gtw-fit flex flex-col bg-brand-primary font-body`) inline. That is fine
// while there are two pages that both happen to copy it — and it silently
// fails the moment a third surface is added, because there is nothing to
// import. That is exactly what happened to the party surface: `PartyPage`
// wrote its own envelope, picked `bg-brand-surface` (#f9f9f9) instead of the
// brand yellow, and rendered no header at all, so /doodleduel lost the whole
// brand identity while every other route kept it.
//
// So the chrome is now ONE component that owns the header markup and the page
// envelope, and every surface renders inside it. A new page cannot forget the
// header, because forgetting it means not using the wrapper at all.
//
// The header is deliberately dumb: brand mark + wordmark on the left, and a
// caller-supplied `right` slot. Nothing app-specific lives here — the trivia
// house-hunt door and the player pill are passed IN by TrivianightPage, so
// the hub's "wordmark and nothing else" rule (F4) stays enforceable by
// reading HomePage.tsx.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, type ReactNode } from "react";
// § F115 — the header line shares the app's ONE column (see `lib/column.ts`).
import { APP_COLUMN, APP_COLUMN_PAD } from "../lib/column";
import { Link, useLocation } from "react-router-dom";
import { PartyPopper } from "lucide-react";
import { GroupsChip } from "./GroupsChip";
import { PlayerChip } from "./PlayerChip";

export interface TopHeaderProps {
  /** Page-supplied controls pinned to the right of the wordmark. */
  right?: ReactNode;
  /**
   * § F97/§ F103 — A GAME SCREEN'S HEADER LINE IS THE GAME'S, on every
   * device. Jake's final sketch (2026-08-20): *"<home icon> <back chevron>
   * <game name> <help icon> [<timer>] [<undo icon>] [<hint>] <player icon>"*.
   * When a page supplies this, it renders in the wordmark's spot everywhere —
   * the wordmark yields on game screens; non-game screens keep it.
   */
  title?: ReactNode;
  /**
   * § F190 — a sentence that belongs on the header line, between the game's
   * icons and the player chip: the one piece of free width on a game screen.
   * Head-to-head's "You are guessing X's word." lives here so it costs the
   * board no height (Jake: *"The play field is squished again."*).
   */
  status?: ReactNode;
  /**
   * § F97 — the game screens continue the header with their own second line
   * (timer + controls, owned by the shell, sticky directly below). On coarse
   * pointers the header's own bottom rule steps aside so the two lines read
   * as ONE header block; the shell's bar carries the rule instead.
   */
  flush?: boolean;
  /**
   * § F108/§ F111 — WHICH COLUMN THE HEADER LINE SHARES.
   *
   * Default (`undefined`) is the app-wide header: its own `max-w-3xl` envelope,
   * which every non-daily challenges surface keeps.
   *
   * `"game"` mirrors `DailyChallengeShell`'s board column (`px-2 sm:px-6` around
   * `max-w-md`) — Jake, 2026-08-21: *"Across all games: the header icons should
   * be no wider left or right than the main game board."*
   *
   * `"menu"` mirrors the daily challenges menus' own column (`px-3` rising to `px-6`
   * above 700px of height, around the same `max-w-md` § F44 cap) — *"In the
   * daily challenges list, the header should be within the left and right boundaries
   * of the daily challenge list … the player icon should not be further right than
   * the right-most of the game list."*
   *
   * `"hub"` mirrors the hub's four-door column (`p-3` rising to `p-6` above
   * 700px of height, around `max-w-md` widening to `max-w-lg` from 600px) —
   * *"Main menu still has the go team williams icon and text further left than
   * the game selection."*
   *
   * `"wide"` mirrors trivia's main column (`px-4 sm:px-6` around `max-w-3xl`),
   * and `"menu"` covers trivia's gate and picker, whose card column is the same
   * `max-w-md` the daily challenges menus use — Jake, 2026-08-21: *"Align Go Team
   * Williams with the left border of the main Trivia window."*
   *
   * In every case the header row's box is the CONTENT's box, at every width, so
   * the two share their left and right edges instead of merely looking close.
   */
  column?: "game" | "menu" | "hub" | "wide";
  /**
   * § F158 (2026-09-06) — BRAND ONLY: no player chip, no `right` slot.
   *
   * § F201 — set by the screens you reach WITHOUT being signed in (sign in,
   * sign up, terms, privacy, an invite): nobody is playing there, and a screen
   * whose job is one question gets no tappable extras. They keep the mark and
   * the wordmark, because a sign-in screen with no branding is a phishing page.
   */
  brandOnly?: boolean;
}

/**
 * The persistent header. Sticky, brand yellow, heavy bottom rule — identical
 * on the hub, the trivia app and the party surface, on every screen of each.
 */
/**
 * § F187 (2026-09-25) — THE HEADER GETS OUT OF THE WAY WHILE YOU SCROLL DOWN.
 *
 * Jake: *"the header needs to collapse when scrolling down, and expand when
 * scrolling up."* One listener, on the DOCUMENT in the capture phase, because
 * this app scrolls in two places: the window on unlocked surfaces, and the
 * `<main>` of a locked one (R2-4) — scroll events do not bubble, but they do
 * capture. Only those two count: a rules panel or an overlay list scrolling
 * inside a card is not the page moving.
 *
 * Within a header's height of the top it is always shown, so nothing can be
 * stranded with the header gone and nowhere left to scroll up from. A toggle
 * ignores the scroll events of its own animation, which is what keeps the
 * window's layout shift (and the browser's scroll anchoring) from reading as a
 * scroll the other way and bouncing it straight back.
 */
const HEADER_PX = 60;
function useCollapseOnScroll(): boolean {
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    const last = new WeakMap<EventTarget, number>();
    let quietUntil = 0;
    let state = false;
    const set = (next: boolean) => {
      if (next === state) return;
      state = next;
      quietUntil = performance.now() + 350;
      setCollapsed(next);
      document.documentElement.classList.toggle("gtw-header-collapsed", next);
    };
    const onScroll = (event: Event) => {
      const target = event.target;
      const isPage = target === document || target === document.documentElement || target === document.body;
      const isMain = target instanceof HTMLElement && target.tagName === "MAIN";
      if (!isPage && !isMain) return;
      const y = isPage ? window.scrollY : (target as HTMLElement).scrollTop;
      const key = isPage ? document : (target as EventTarget);
      const prev = last.get(key) ?? 0;
      last.set(key, y);
      if (performance.now() < quietUntil) return;
      if (y <= HEADER_PX) return set(false);
      if (y - prev > 4) set(true);
      else if (prev - y > 4) set(false);
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => {
      document.removeEventListener("scroll", onScroll, { capture: true });
      document.documentElement.classList.remove("gtw-header-collapsed");
    };
  }, []);
  return collapsed;
}

export function TopHeader({ right, title, status, flush = false, column, brandOnly = false }: TopHeaderProps) {
  const collapsed = useCollapseOnScroll();
  // F39: the brand mark is the way back to the hub from any surface. On the
  // hub itself it stays inert — a self-link would put a tappable element in
  // the hub header, which F4 forbids ("the doors are the only tappable
  // things on /").
  const onHub = useLocation().pathname === "/";
  /*
   * § F158 — AND ON THE GATE. The mark is a `<Link to="/">` everywhere else,
   * which on the password screen is the one control able to destroy a deep
   * link: tap it while gated and `/doodleduel?code=ABCD` becomes `/`, still
   * gated, code gone. `brandOnly` makes the mark inert for the same reason the
   * hub does — there is nowhere for it to go from here.
   */
  const inertMark = onHub || brandOnly;
  // § F103 — on a game screen (title present) the mark is one of the line's
  // uniform icons, sized by `.gtw-hicon`; everywhere else it keeps its 36px.
  const markTile = (
    <div
      className={`${
        title ? "gtw-hicon" : "w-9 h-9 flex-shrink-0"
      } rounded-block bg-white ink-border flex items-center justify-center`}
    >
      <PartyPopper className="w-4 h-4 text-black" strokeWidth={2.5} />
    </div>
  );
  return (
    <header
      data-testid="chrome-header"
      data-collapsed={collapsed ? "1" : "0"}
      className={`gtw-header shrink-0 sticky top-0 z-40 bg-brand-primary border-b-4 border-black ${
        flush ? "gtw-flush" : ""
      }`}
    >
      {/* § F108 (2026-08-21) — ON A GAME SCREEN THE LINE IS THE BOARD'S WIDTH.
          Jake: *"Across all games: the header icons should be no wider left or
          right than the main game board."* The header used to be its own
          `max-w-3xl` envelope, so on a laptop the home mark and the player icon
          sat a hand away from a 448px board. The wrapper below now mirrors the
          shell's own column EXACTLY — same outer padding (`px-2 sm:px-6`,
          DailyChallengeShell's `<main>`) around the same `max-w-md` cap — so the two
          boxes share their left and right edges at every width. Every other
          screen keeps the wide header. */}
      <div
        className={
          column === "game"
            ? "px-2 sm:px-6"
            : column === "wide"
              ? "px-4 sm:px-6"
              : column === "menu" || column === "hub"
                ? APP_COLUMN_PAD
                : "max-w-3xl mx-auto px-3 sm:px-6"
        }
      >
      <div
        data-testid={column ? "chrome-game-line" : undefined}
        className={`${
          /* § F115 — "menu" and "hub" are the same column now: the app has one
             (`APP_COLUMN`), and every front door uses it. "game" stays the
             board's `max-w-md` (§ F108) and "wide" stays trivia's own. */
          column === "hub" || column === "menu"
            ? `${APP_COLUMN} mx-auto`
            : column === "wide"
              ? "w-full max-w-3xl mx-auto"
              : column === "game"
                ? "w-full max-w-md mx-auto"
                : ""
        } h-14 flex items-center justify-between ${title ? "gap-1" : "gap-2"}`}
      >
        {/* § F157 — ON A GAME SCREEN THIS GROUP MUST NOT BE THE SHRINK VICTIM.
            It holds nothing but fixed-size icons (home · back · mark), so
            `flex-1` gave the flex algorithm a box it could squeeze below its
            content — and its `flex-shrink: 0` children then overflowed it and
            painted over the timer, which is what Jake filmed at 320px. `justify
            -between` on the line already pins this group left and the controls
            right, so the growth was never doing any work. The wordmark branch
            keeps `flex-1 min-w-0`: that one has TEXT to truncate. */}
        <div
          className={`flex items-center min-w-0 ${title ? "flex-none" : "flex-1"} ${
            title ? "gap-1" : "gap-2"
          }`}
        >
          {inertMark ? (
            markTile
          ) : (
            <Link
              to="/"
              aria-label="Go Team Williams — home"
              data-testid="chrome-mark-home"
              /* F39 pins a ≥44px tap target on non-game screens (the 36px
                 tile plus this padding); a game screen's line is uniform
                 `gtw-hicon` sizes instead (§ F103). */
              className={`btn-press flex-shrink-0 ${title ? "" : "p-1 -m-1"}`}
            >
              {markTile}
            </Link>
          )}
          {/* § F103 — the wordmark yields to the game's own header content on
              EVERY device now (Jake's one-line sketch has no wordmark); every
              non-game screen keeps it. The interactive piece inside `title`
              carries its OWN testid (`chrome-game-back`), so no locator ever
              resolves twice. */}
          {title ? (
            <div
              data-testid="chrome-game-title"
              /* § F157 — fixed-size icons, so `flex-none`: see above. */
              className="min-w-0 flex-none flex items-center gap-0.5"
            >
              {title}
            </div>
          ) : (
            <span className="font-heading font-extrabold text-sm sm:text-base leading-none tracking-tight truncate">
              Go Team Williams
            </span>
          )}
        </div>
        {/* § F117 — THE PLAYER IS THE CHROME'S, NOT EACH PAGE'S. Jake: *"if my
            player is selected then I should see it across all screens to know I
            am selected."* The chip draws itself only when somebody is picked,
            and it is the LAST thing on the line (§ F105: the player icon is
            always the far right). A page that wants controls puts them in
            `right`; they land to its left. */}
        {status !== undefined && (
          <div data-testid="chrome-status" className="min-w-0 flex-1 px-1">
            {status}
          </div>
        )}
        {/* § F158 — the gate draws neither: see `brandOnly` above. */}
        {brandOnly ? null : (
        <div className={`flex items-center ${title ? "gap-1" : "gap-2"}`}>
          {right}
          {/* § F208 — the Groups icon, on the hub and menus. A game screen's
              ONE line (§ F103/§ F157) is already full at 320px. */}
          {title ? null : <GroupsChip />}
          <PlayerChip />
        </div>
        )}
      </div>
      </div>
    </header>
  );
}

export interface AppChromeProps {
  /** Right-hand header slot, forwarded to `TopHeader`. */
  right?: ReactNode;
  /** § F97 — game-screen line-1 content, forwarded to `TopHeader`. */
  title?: ReactNode;
  /** § F190 — a sentence on the header line, forwarded to `TopHeader`. */
  status?: ReactNode;
  /** § F97 — drop the header rule on coarse pointers, forwarded to `TopHeader`. */
  flush?: boolean;
  /** § F108/§ F111 — which column the header line shares, forwarded to `TopHeader`. */
  column?: "game" | "menu" | "hub" | "wide";
  /** § F158 — brand mark and wordmark only, forwarded to `TopHeader`. */
  brandOnly?: boolean;
  /**
   * R2-4 (2026-08-06) — MAKE THE ENVELOPE A CEILING.
   *
   * Default (`false`) is the envelope this app has always had: `.gtw-fit`, a
   * `min-height` floor, page grows, page scrolls. Set it on a MENU and the
   * envelope becomes `height: 100dvh; overflow: hidden` as well, which is what
   * gives every `flex-1 min-h-0` child below a real height to divide.
   *
   * GAME BOARDS MUST NOT SET IT. A board is a drawable square (F31.d); clipping
   * one is worse than scrolling it, and Jake's ask was about menus. The rule is
   * asserted in `tests/daily-challenges-ui-contract.test.ts` § R2-4.
   */
  lock?: boolean;
  children: ReactNode;
}

/**
 * The full-height brand envelope: yellow page, body font, header pinned at the
 * top, caller's content below it in the remaining space. Callers supply their
 * own `<main>` so each surface keeps its own column width and padding.
 */
export function AppChrome({ right, title, status, flush, column, lock = false, brandOnly = false, children }: AppChromeProps) {
  // `gtw-lock` is ADDITIVE — see the block comment beside it in `index.css`.
  // Keeping `gtw-fit` on a locked surface means the app-wide "this is the brand
  // envelope" selector still finds every page, and the two rules compute to the
  // same 100dvh rather than fighting.
  return lock ? (
    <div className="gtw-fit gtw-lock flex flex-col bg-brand-primary font-body">
      <TopHeader right={right} title={title} status={status} flush={flush} column={column} brandOnly={brandOnly} />
      {children}
    </div>
  ) : (
    <div className="gtw-fit flex flex-col bg-brand-primary font-body">
      <TopHeader right={right} title={title} status={status} flush={flush} column={column} brandOnly={brandOnly} />
      {children}
    </div>
  );
}

export default AppChrome;
